/**
 * Shared vocabulary: buttons, states, counts, time.
 *
 * Kept deliberately small. A string that only one surface uses belongs to
 * that surface's namespace — `common` is for words that appear in two or more
 * places, and for the handful of nouns ("Words", "Topics", "Review") that the
 * whole product leans on. The test for adding something here is "would a
 * translator find it here or by hunting?"
 */
export const common = {
  /** Destructive-toast title used across surfaces. */
  error: "Error",
  actions: {
    save: "Save",
    cancel: "Cancel",
    close: "Close",
    delete: "Delete",
    edit: "Edit",
    create: "Create",
    add: "Add",
    remove: "Remove",
    update: "Update",
    confirm: "Confirm",
    back: "Back",
    next: "Next",
    previous: "Previous",
    finish: "Finish",
    submit: "Submit",
    retry: "Try again",
    copy: "Copy",
    copied: "Copied",
    share: "Share",
    download: "Download",
    upload: "Upload",
    search: "Search",
    clearSearch: "Clear search",
    filter: "Filter",
    clear: "Clear",
    clearAll: "Clear all",
    selectAll: "Select all",
    apply: "Apply",
    reset: "Reset",
    continue: "Continue",
    start: "Start",
    pause: "Pause",
    resume: "Resume",
    skip: "Skip",
    done: "Done",
    seeAll: "See all",
    showMore: "Show more",
    showLess: "Show less",
    learnMore: "Learn more",
    viewAll: "View all",
    loading: "Loading…",
    saving: "Saving…",
    send: "Send",
    signIn: "Sign in",
    signUp: "Sign up",
    signOut: "Sign out",
    /** Collect a reward that is ready — a streak milestone, a finished challenge. */
    claim: "Claim",
    /** Back to the current day from a calendar showing another month. */
    today: "Today",
  },
  states: {
    loading: "Loading…",
    error: "Something went wrong",
    empty: "Nothing here yet",
    noResults: "No results found",
    offline: "You're offline",
    errorRetry: "That didn't work. Try again.",
    notFoundTitle: "Page not found",
    notFoundDescription: "Sorry, we couldn't find the page you're looking for. It might have been moved or doesn't exist.",
    goBack: "Go Back",
    backToHome: "Back to Home",
    pageLabel: "404 page",
  },
  mascot: {
    /** Accessible name for the floating assistant button. */
    openLabel: "Open Learning Coach or Dictionary",
  },
  labels: {
    loading: "Loading",
    optional: "optional",
    required: "required",
    words: "Words",
    word: "Word",
    topics: "Topics",
    topic: "Topic",
    questions: "Questions",
    question: "Question",
    vocab: "Vocab",
    minutes: "min",
    level: "Level",
  },
  /** i18next plural keys: `_one` / `_other` for English. */
  count: {
    word_one: "{{count}} word",
    word_other: "{{count}} words",
    topic_one: "{{count}} topic",
    topic_other: "{{count}} topics",
    question_one: "{{count}} question",
    question_other: "{{count}} questions",
    vocab_one: "{{count}} vocab",
    vocab_other: "{{count}} vocab",
    video_one: "{{count}} video",
    video_other: "{{count}} videos",
    views_one: "{{count}} view",
    views_other: "{{count}} views",
    likes_one: "{{count}} like",
    likes_other: "{{count}} likes",
    comments_one: "{{count}} comment",
    comments_other: "{{count}} comments",
    item_one: "{{count}} item",
    item_other: "{{count}} items",
  },
  time: {
    justNow: "just now",
    today: "Today",
    yesterday: "Yesterday",
    week_short: "wk",
    day_short: "d",
  },
  errors: {
    generic: "Something went wrong. Please try again.",
    network: "Network error. Check your connection.",
    unauthorized: "Please sign in to continue.",
    notFound: "We couldn't find that.",
    /** Shape every edge-function error description should follow. */
    tryAgain: "Please try again.",
  },
} as const;